import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { VelavuAPI, VelavuFloor, VelavuSite } from "velavu-js-api";
import IconDelete from "../../../../../dynamicicons/icon-delete";
import IconEdit from "../../../../../dynamicicons/icon-edit";
import SelectableCard from "../../../../../elements/selectable-card";
import VelavuButton from "../../../../../elements/velavu-button";
import VelavuMenu, {
	VelavuMenuItem,
} from "../../../../../elements/velavu-menu";
import LoadingDots from "../../../../../elements/loading-dots";
import { useVelavuModal } from "../../../../../elements/velavu-modal";
import VelavuAlertModal from "../../../../../elements/velavu-alert-modal/velavu-alert-modal";
import VelavuAlertModalTitle from "../../../../../elements/velavu-alert-modal/velavu-alert-modal-title";
import VelavuAlertModalContent from "../../../../../elements/velavu-alert-modal/velavu-alert-modal-content";
import VelavuAlertModalActions from "../../../../../elements/velavu-alert-modal/velavu-alert-modal-actions";
import * as styles from "./site-detail-floors.module.scss";
import { NoFloorsIcon } from "../../../../../img/icons/icons";
import { PendingFloorDuplication } from "../../../../../state/floor/floor-duplicate";
import FloorListEntry, {
	floorListEntryFromFloor,
	floorListEntryFromPendingFloorDuplication,
} from "./floor-list-entry";
import SelectableCardFloor from "./selectable-card-floor";
import IconCopy from "../../../../../dynamicicons/icon-copy";
import VelavuAlertModalObject from "../../../../../elements/velavu-alert-modal/velavu-alert-modal-object";
import { useMappedinIntegrationContext } from "../../../../main/mappedin-integration-provider";
import { useHasPermission } from "../../../../sign-in-context/sign-in-context";

interface SiteDetailFloorsProps {
	site: VelavuSite;

	selectedFloor: string | undefined;
	onSelectFloor?: (id: string | undefined) => void;

	onDeletePlan?: (floor: VelavuFloor) => void;
	onEditFloor?: (floor: VelavuFloor) => void;
	onCreateFloor?: () => void;
	onDuplicateFloor?: (floor: VelavuFloor) => void;
	floorDuplications: PendingFloorDuplication[];
}

function NoFloors() {
	const { t } = useTranslation();

	return (
		<div className={styles.emptyContent}>
			<NoFloorsIcon />
			<span className={styles.heading}>
				{t("dashboard.site_floors.no_floorplan")}
			</span>
			<span className={styles.info}>
				{t("dashboard.site_floors.no_floorplan_hint")}
			</span>
		</div>
	);
}

export default function SiteDetailFloors(props: SiteDetailFloorsProps) {
	const { t } = useTranslation();
	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const { pushModal } = useVelavuModal();
	const { siteVenueMap } = useMappedinIntegrationContext();
	const isMappedinImportedSite = siteVenueMap.has(props.site.id);

	const canWriteSites = useHasPermission("sites:write");

	const {
		site: propsSite,
		onSelectFloor: propsOnSelectFloor,
		onCreateFloor: propsOnCreateFloor,
		onEditFloor: propsOnEditFloor,
		onDeletePlan: propsOnDeletePlan,
		onDuplicateFloor: propsOnDuplicateFloor,
	} = props;

	const activeMenuFloorID = useRef<string>("");

	const openMenu = useCallback(
		(event: React.MouseEvent<HTMLElement>, floorID: string) => {
			setMenuAnchor(event.currentTarget);
			activeMenuFloorID.current = floorID;
		},
		[setMenuAnchor],
	);

	const closeMenu = useCallback(() => {
		setMenuAnchor(null);
	}, [setMenuAnchor]);

	const editFloorPlan = useCallback(() => {
		closeMenu();

		const floor = propsSite.floors!.find(
			(floor) => floor.id === activeMenuFloorID.current,
		)!;

		propsOnEditFloor?.(floor);
	}, [closeMenu, propsOnEditFloor, propsSite]);

	const duplicateFloorPlan = useCallback(() => {
		closeMenu();

		const floorID = activeMenuFloorID.current;

		const floor = propsSite.floors!.find((floor) => floor.id === floorID)!;

		propsOnDuplicateFloor?.(floor);
	}, [closeMenu, propsOnDuplicateFloor, propsSite]);

	const deleteFloorPlan = useCallback(() => {
		closeMenu();

		const floorID = activeMenuFloorID.current;

		const floor = propsSite.floors!.find((floor) => floor.id === floorID)!;
		pushModal<boolean>((resolve) => (
			<VelavuAlertModal>
				<VelavuAlertModalTitle>
					{t("dashboard.site_floors.delete.title")}
				</VelavuAlertModalTitle>

				<VelavuAlertModalObject>{floor.name}</VelavuAlertModalObject>

				<VelavuAlertModalContent>
					{t("dashboard.site_floors.delete.body")}
				</VelavuAlertModalContent>

				<VelavuAlertModalActions>
					<VelavuButton
						label={t("common.cancel")}
						variant="outlined"
						fullWidth
						onClick={() => resolve(false)}
					/>

					<VelavuButton
						label={t("common.delete")}
						variant="danger"
						fullWidth
						onClick={() => resolve(true)}
					/>
				</VelavuAlertModalActions>
			</VelavuAlertModal>
		)).then((result) => {
			if (result) {
				VelavuAPI.siteFloors
					.deleteSiteFloor(propsSite.id, floorID)
					.then(() => {
						propsOnSelectFloor?.(undefined);
						propsOnDeletePlan?.(floor);
					})
					.catch(() =>
						alert(
							t("dashboard.site_floors.delete.failed", {
								id: floor.name,
							}),
						),
					);
			}
		});
	}, [
		closeMenu,
		pushModal,
		propsOnDeletePlan,
		propsSite,
		propsOnSelectFloor,
		t,
	]);

	const floorEntries = useMemo(() => {
		const entries: FloorListEntry[] = [];
		if (props.site.floors !== undefined) {
			entries.push(...props.site.floors.map(floorListEntryFromFloor));
		}
		entries.push(
			...props.floorDuplications.map(
				floorListEntryFromPendingFloorDuplication,
			),
		);
		entries.sort((a, b) => b.level - a.level);
		return entries;
	}, [props.site.floors, props.floorDuplications]);

	let result;
	if (!propsSite?.floors?.length) {
		result = <NoFloors />;
	} else {
		result = (
			<div className={styles.list}>
				{floorEntries.map((entry) => (
					<SelectableCardFloor
						key={entry.id}
						entry={entry}
						selected={
							entry.floorID !== null &&
							props.selectedFloor === entry.floorID
						}
						onClick={() => {
							if (
								entry.floorID !== null &&
								props.selectedFloor !== entry.floorID
							) {
								props.onSelectFloor?.(entry.floorID);
							}
						}}
						onClickMore={
							isMappedinImportedSite || !canWriteSites
								? undefined
								: (event) => {
										if (entry.floorID !== null) {
											openMenu(event, entry.floorID);
										}
									}
						}
					/>
				))}
			</div>
		);
	}

	return (
		<>
			<VelavuMenu
				open={!!menuAnchor}
				onClose={closeMenu}
				refPosition={menuAnchor}
			>
				<VelavuMenuItem
					onClick={editFloorPlan}
					icon={(size, color) => (
						<IconEdit size={size} color={color} />
					)}
					label={t("common.edit_floor_plan")}
				/>
				<VelavuMenuItem
					onClick={duplicateFloorPlan}
					icon={(size, color) => (
						<IconCopy size={size} color={color} />
					)}
					label={t("common.duplicate_floor_plan")}
				/>
				<VelavuMenuItem
					onClick={deleteFloorPlan}
					icon={(size, color) => (
						<IconDelete size={size} color={color} />
					)}
					label={t("common.delete_floor_plan")}
					danger
				/>
			</VelavuMenu>

			<div className={styles.content}>
				{result}
				{!isMappedinImportedSite && canWriteSites && (
					<div className={styles.button}>
						<VelavuButton
							fullWidth
							label={t("common.add_floor_plan")}
							onClick={propsOnCreateFloor}
						/>
					</div>
				)}
			</div>
		</>
	);
}
